import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ImageUploader from "../../components/common/ImageUploader";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const blank = {
  shop_name: "", bio: "", logo_url: "", banner_url: "",
  legal_name: "", business_type: "", gstin: "", pan: "",
  owner_name: "", contact_email: "", contact_phone: "",
  business_address: { line1: "", line2: "", city: "", state: "", pincode: "", country: "IN" },
  pickup_address: { line1: "", line2: "", city: "", state: "", pincode: "", country: "IN" },
  bank: { account_holder: "", account_number: "", ifsc: "", bank_name: "" },
  kyc_documents: [],
  return_policy: "",
  shipping_settings: { domestic_charge: 0, free_above: 0, processing_days: 2 },
  business_hours: { mon: "10-19", tue: "10-19", wed: "10-19", thu: "10-19", fri: "10-19", sat: "10-19", sun: "closed" },
};

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4">
      <div className="overline">{title}</div>
      {children}
    </div>
  );
}

export default function SellerSettings() {
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState({ razorpay_key_id: "", razorpay_key_secret: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/onboarding");
      setForm({
        ...blank,
        ...data,
        business_address: { ...blank.business_address, ...(data.business_address || {}) },
        pickup_address: { ...blank.pickup_address, ...(data.pickup_address || {}) },
        bank: { ...blank.bank, ...(data.bank || {}) },
        shipping_settings: { ...blank.shipping_settings, ...(data.shipping_settings || {}) },
        business_hours: { ...blank.business_hours, ...(data.business_hours || {}) },
        kyc_documents: data.kyc_documents || [],
      });
      setKeys({ razorpay_key_id: data.razorpay_key_id || "", razorpay_key_secret: "" });
    } catch (e) { toast.error("Failed to load profile"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.shop_name?.trim()) return toast.error("Shop name required");
    setSaving(true);
    try {
      await api.put("/seller/onboarding", form);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveKeys = async () => {
    setSaving(true);
    try {
      await api.put("/sellers/me/keys", keys);
      toast.success("Razorpay keys updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div data-testid="seller-settings-loading">Loading…</div>;

  const set = (path, value) => {
    setForm((f) => {
      const parts = path.split(".");
      const next = { ...f };
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="seller-settings-page">
      <div>
        <div className="overline mb-2">Storefront</div>
        <h1 className="font-display text-4xl">Shop Settings</h1>
      </div>

      <Section title="Storefront Identity">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-1 block">Shop Name</label>
            <input className="kb-input" value={form.shop_name} onChange={(e) => set("shop_name", e.target.value)} data-testid="ss-name" />
          </div>
          <div>
            <label className="overline mb-1 block">Owner Name</label>
            <input className="kb-input" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="ss-owner" />
          </div>
        </div>
        <div>
          <label className="overline mb-1 block">About / Bio</label>
          <textarea rows={3} className="kb-input" value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="ss-bio" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-2 block">Logo</label>
            <ImageUploader value={form.logo_url ? [form.logo_url] : []} onChange={(arr) => set("logo_url", arr[0] || null)} max={1} testid="ss-logo" />
          </div>
          <div>
            <label className="overline mb-2 block">Banner</label>
            <ImageUploader value={form.banner_url ? [form.banner_url] : []} onChange={(arr) => set("banner_url", arr[0] || null)} max={1} testid="ss-banner" />
          </div>
        </div>
      </Section>

      <Section title="Contact">
        <div className="grid md:grid-cols-2 gap-4">
          <input className="kb-input" placeholder="Email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="ss-email" />
          <input className="kb-input" placeholder="Phone" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="ss-phone" />
        </div>
      </Section>

      <Section title="Compliance / KYC">
        <div className="grid md:grid-cols-2 gap-4">
          <input className="kb-input" placeholder="Legal Name" value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} data-testid="ss-legal" />
          <select className="kb-input" value={form.business_type || ""} onChange={(e) => set("business_type", e.target.value)} data-testid="ss-biz-type">
            <option value="">Business Type…</option>
            <option value="individual">Individual / Proprietor</option>
            <option value="llp">LLP</option>
            <option value="pvtltd">Pvt Ltd</option>
            <option value="partnership">Partnership</option>
          </select>
          <input className="kb-input" placeholder="GSTIN" value={form.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} data-testid="ss-gst" />
          <input className="kb-input" placeholder="PAN" value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} data-testid="ss-pan" />
        </div>
        <div>
          <label className="overline mb-2 block">KYC Documents (GST cert, PAN, cancelled cheque)</label>
          <ImageUploader value={form.kyc_documents || []} onChange={(arr) => set("kyc_documents", arr)} max={6} testid="ss-kyc" />
        </div>
      </Section>

      {["business_address", "pickup_address"].map((key) => (
        <Section key={key} title={key === "business_address" ? "Business Address" : "Pickup Address"}>
          <div className="grid md:grid-cols-2 gap-4">
            <input className="kb-input md:col-span-2" placeholder="Address line 1" value={form[key]?.line1 || ""} onChange={(e) => set(`${key}.line1`, e.target.value)} data-testid={`ss-${key}-line1`} />
            <input className="kb-input md:col-span-2" placeholder="Address line 2 (optional)" value={form[key]?.line2 || ""} onChange={(e) => set(`${key}.line2`, e.target.value)} />
            <input className="kb-input" placeholder="City" value={form[key]?.city || ""} onChange={(e) => set(`${key}.city`, e.target.value)} />
            <input className="kb-input" placeholder="State" value={form[key]?.state || ""} onChange={(e) => set(`${key}.state`, e.target.value)} />
            <input className="kb-input" placeholder="Pincode" value={form[key]?.pincode || ""} onChange={(e) => set(`${key}.pincode`, e.target.value)} />
            <input className="kb-input" placeholder="Country" value={form[key]?.country || "IN"} onChange={(e) => set(`${key}.country`, e.target.value)} />
          </div>
        </Section>
      ))}

      <Section title="Bank Details">
        <div className="grid md:grid-cols-2 gap-4">
          <input className="kb-input" placeholder="Account Holder" value={form.bank?.account_holder || ""} onChange={(e) => set("bank.account_holder", e.target.value)} data-testid="ss-bank-holder" />
          <input className="kb-input" placeholder="Bank Name" value={form.bank?.bank_name || ""} onChange={(e) => set("bank.bank_name", e.target.value)} />
          <input className="kb-input" placeholder="Account Number" value={form.bank?.account_number || ""} onChange={(e) => set("bank.account_number", e.target.value)} data-testid="ss-bank-acc" />
          <input className="kb-input" placeholder="IFSC" value={form.bank?.ifsc || ""} onChange={(e) => set("bank.ifsc", e.target.value.toUpperCase())} data-testid="ss-bank-ifsc" />
        </div>
      </Section>

      <Section title="Return Policy">
        <textarea rows={4} className="kb-input" placeholder="Describe your returns & exchange policy…" value={form.return_policy || ""} onChange={(e) => set("return_policy", e.target.value)} data-testid="ss-return-policy" />
      </Section>

      <Section title="Shipping">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="overline mb-1 block">Domestic Charge (₹)</label>
            <input type="number" className="kb-input" value={form.shipping_settings?.domestic_charge ?? 0} onChange={(e) => set("shipping_settings.domestic_charge", Number(e.target.value) || 0)} data-testid="ss-ship-charge" />
          </div>
          <div>
            <label className="overline mb-1 block">Free Above (₹)</label>
            <input type="number" className="kb-input" value={form.shipping_settings?.free_above ?? 0} onChange={(e) => set("shipping_settings.free_above", Number(e.target.value) || 0)} />
          </div>
          <div>
            <label className="overline mb-1 block">Processing Days</label>
            <input type="number" className="kb-input" value={form.shipping_settings?.processing_days ?? 2} onChange={(e) => set("shipping_settings.processing_days", Number(e.target.value) || 0)} />
          </div>
        </div>
      </Section>

      <Section title="Business Hours">
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {DAYS.map((d) => (
            <div key={d}>
              <label className="overline mb-1 block">{d.toUpperCase()}</label>
              <input className="kb-input" placeholder="10-19 or closed" value={form.business_hours?.[d] || ""} onChange={(e) => set(`business_hours.${d}`, e.target.value)} data-testid={`ss-hours-${d}`} />
            </div>
          ))}
        </div>
      </Section>

      <div className="flex justify-end">
        <button className="kb-btn-primary" onClick={save} disabled={saving} data-testid="ss-save">{saving ? "Saving…" : "Save All Settings"}</button>
      </div>

      <Section title="Razorpay Keys">
        <p className="text-sm text-[hsl(var(--kb-muted))]">Customers pay directly into your Razorpay account; we deduct platform fees after settlement.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <input className="kb-input" placeholder="rzp_live_XXXX" value={keys.razorpay_key_id} onChange={(e) => setKeys({ ...keys, razorpay_key_id: e.target.value })} data-testid="rzp-key-id" />
          <input type="password" className="kb-input" placeholder="••••••••" value={keys.razorpay_key_secret} onChange={(e) => setKeys({ ...keys, razorpay_key_secret: e.target.value })} data-testid="rzp-key-secret" />
        </div>
        <button className="kb-btn-primary" onClick={saveKeys} disabled={saving} data-testid="rzp-save">Save Keys</button>
      </Section>
    </div>
  );
}
